<template>
  <div
    class="Faqs"
    :class="{
      'Faqs--categorized': hasCategories,
      'Faqs--withAside': $slots.aside,
    }"
  >
    <div
      v-if="eyebrow || title || description || $slots.fullFaqLink"
      class="Faqs-header"
    >
      <Eyebrow
        v-if="eyebrow"
        class="Faqs-eyebrow"
        :label="eyebrow"
        variant="secondary"
      />

      <h2 v-if="title" class="Faqs-title">
        {{ title }}
      </h2>

      <p
        v-if="description"
        class="Faqs-description"
        v-html="description"
      />

      <p v-if="$slots.fullFaqLink" class="Faqs-subtitle">
        {{ t('ui.components.faqs.cannotFind') }}

        <slot name="fullFaqLink" />
      </p>
    </div>

    <SearchBar
      v-if="isSearchable"
      :id="searchInputId"
      v-model="query"
      class="Faqs-search"
      name="faqSearch"
      :placeholder="t('ui.components.faqs.searchPlaceholder')"
      :ariaLabel="t('ui.components.faqs.searchAriaLabel')"
      :clearAriaLabel="t('ui.components.faqs.searchClearAriaLabel')"
      :submitAriaLabel="t('ui.components.faqs.searchSubmitAriaLabel')"
      :micAriaLabel="t('ui.components.faqs.searchMicAriaLabel')"
      :micErrorMessage="t('ui.components.faqs.searchMicError')"
    />

    <!-- Announces the match count, because the results themselves replace the rail silently.
         It stays mounted so assistive tech is already watching when the first query lands, and
         it carries only the count: a live region holding the answers would read them all back. -->
    <p
      v-if="isSearchable"
      class="u-visually-hidden"
      role="status"
      aria-live="polite"
    >
      {{ searchStatus }}
    </p>

    <div v-if="isSearching" class="Faqs-results">
      <template v-if="bestMatchItems.length">
        <p class="Faqs-resultsLabel">
          {{ t('ui.components.faqs.bestMatch') }}
        </p>

        <Accordion
          v-model="openBestMatch"
          class="Faqs-bestMatch"
          :items="bestMatchItems"
          type="multiple"
          collapsible
        />

        <template v-if="otherMatchItems.length">
          <p class="Faqs-resultsLabel">
            {{ t('ui.components.faqs.otherMatches', { count: otherMatchItems.length }, otherMatchItems.length) }}
          </p>

          <Accordion
            v-model="openOtherMatches"
            :items="otherMatchItems"
            type="multiple"
            collapsible
          />
        </template>
      </template>

      <div v-else class="Faqs-noResults">
        <Icon
          class="Faqs-noResultsIcon"
          icon="question_mark"
          size="xl"
        />

        <div class="Faqs-noResultsContent">
          <p class="Faqs-noResultsTitle">
            {{ t('ui.components.faqs.noResults.title', { query: query.trim() }) }}
          </p>

          <p class="Faqs-noResultsDescription">
            {{ t('ui.components.faqs.noResults.description') }}
          </p>
        </div>

        <div v-if="$slots.noResultsActions" class="Faqs-noResultsActions">
          <slot name="noResultsActions" />
        </div>
      </div>
    </div>

    <TabsRoot
      v-else-if="hasCategories"
      v-model="selectedCategory"
      class="Faqs-body"
      :orientation="railOrientation"
    >
      <Navigation
        ref="categoriesRail"
        :tag="TabsList"
        :items="railEntries"
        :activeId="selectedCategory"
        class="Faqs-categories"
        :aria-label="t('ui.components.faqs.categoriesAriaLabel')"
      >
        <template #item="{ item, buttonProps }">
          <TabsTrigger :value="item.id" asChild>
            <ActionButton v-bind="buttonProps">
              {{ item.label }}
            </ActionButton>
          </TabsTrigger>
        </template>
      </Navigation>

      <ScrollProgressBar v-if="isMobile" :target="railElement" />

      <TabsContent
        v-if="allTopics"
        class="Faqs-panel"
        :value="ALL_TOPICS_ID"
      >
        <section
          v-for="category of visibleCategories"
          :key="category.id"
          class="Faqs-group"
        >
          <h2 class="Faqs-groupHeader">
            <Tag
              type="inverse"
              variant="filled"
              size="lg"
              :leftIcon="category.icon"
            >
              {{ category.label }}
            </Tag>
          </h2>

          <Accordion
            :items="groupedAccordionItems[category.id]"
            type="multiple"
            collapsible
          />
        </section>
      </TabsContent>

      <TabsContent
        v-for="category of visibleCategories"
        :key="category.id"
        class="Faqs-panel"
        :value="category.id"
      >
        <h2 class="u-visually-hidden">
          {{ category.label }}
        </h2>

        <Accordion
          v-model="openItems"
          :items="accordionItems"
          type="multiple"
          collapsible
        />
      </TabsContent>

      <div v-if="$slots.aside" class="Faqs-aside">
        <slot name="aside" />
      </div>
    </TabsRoot>

    <div v-else class="Faqs-body">
      <Accordion
        v-model="openItems"
        :items="accordionItems"
        type="multiple"
        collapsible
      />

      <div v-if="$slots.aside" class="Faqs-aside">
        <slot name="aside" />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, ref, useId, useTemplateRef, watch, type ComponentPublicInstance } from 'vue';
import { useI18n } from '@cvo/i18n/composables/useI18n';
import { TabsContent, TabsList, TabsRoot, TabsTrigger } from 'reka-ui';
import ActionButton from '@cvo/ui/components/atoms/ActionButton/ActionButton.vue';
import Eyebrow from '@cvo/ui/components/atoms/Eyebrow/Eyebrow.vue';
import Icon from '@cvo/ui/components/atoms/Icon/Icon.vue';
import ScrollProgressBar from '@cvo/ui/components/atoms/ScrollProgressBar/ScrollProgressBar.vue';
import Tag from '@cvo/ui/components/atoms/Tag/Tag.vue';
import Navigation from '@cvo/ui/components/molecules/Navigation/Navigation.vue';
import SearchBar from '@cvo/ui/components/molecules/SearchBar/SearchBar.vue';
import { useFaqSearch } from '@cvo/ui/composables/useFaqSearch';
import { useResponsive } from '@cvo/ui/composables/useResponsive';
import type { AccordionItem } from '../../molecules/Accordion/Accordion.model';
import type { FaqCategory, FaqsItem, FaqsProps } from './Faqs.model';

const props = defineProps<FaqsProps>();

const { t } = useI18n();
const { isMobile } = useResponsive();

/** Sentinel rail entry for the grouped all-topics view; not a real category id. */
const ALL_TOPICS_ID = 'faqs-all-topics';

/** How many top results are called out above the rest. Splits the ranked results in two. */
const BEST_MATCH_COUNT = 1;

/** How many questions a capped category teases before the full FAQ page takes over. */
const MAX_ITEMS_PER_CATEGORY = 2;

const selectedCategory = ref<string>();
const openItems = ref<string[]>([]);

// Drives which arrow keys move between categories: a scrolling row on mobile, a rail above it.
const railOrientation = computed(() => (isMobile.value ? 'horizontal' : 'vertical'));

// The rail is the element that scrolls, so the progress bar tracks it.
const categoriesRail = useTemplateRef<ComponentPublicInstance>('categoriesRail');
const railElement = computed<HTMLElement | undefined>(() => categoriesRail.value?.$el);

/** Categories holding no item are not offered. */
const visibleCategories = computed(() => {
  const usedCategories = new Set((props.items || []).map(item => item.category));
  return (props.categories || []).filter(category => usedCategories.has(category.id));
});

const hasCategories = computed(() => visibleCategories.value.length > 0);

/** Rail entries: the categories, preceded by "All topics" when the grouped view is offered. */
const railEntries = computed<FaqCategory[]>(() => {
  if (!props.allTopics || !hasCategories.value) return visibleCategories.value;
  return [{ id: ALL_TOPICS_ID, label: t('ui.components.faqs.allTopics') }, ...visibleCategories.value];
});

const visibleItems = computed(() => {
  if (!selectedCategory.value) return props.items || [];
  return capItems((props.items || []).filter(item => item.category === selectedCategory.value));
});

const accordionItems = computed<AccordionItem[]>(() => {
  return visibleItems.value.map((item, index) => toAccordionItem(item, `faq-${index}`));
});

function toAccordionItem(item: FaqsItem, value: string): AccordionItem {
  return { value, title: item.question, content: item.answer };
}

function capItems(items: FaqsItem[]): FaqsItem[] {
  return props.capped ? items.slice(0, MAX_ITEMS_PER_CATEGORY) : items;
}

// Values are scoped by category id so the groups never collide with each other.
const groupedAccordionItems = computed<Record<string, AccordionItem[]>>(() => {
  const groups: Record<string, AccordionItem[]> = {};
  for (const category of visibleCategories.value) {
    const categoryItems = capItems((props.items || []).filter(item => item.category === category.id));
    groups[category.id] = categoryItems.map((item, index) => toAccordionItem(item, `faq-${category.id}-${index}`));
  }
  return groups;
});

// Selects the first rail entry, and recovers when the selected one stops being offered.
watch(railEntries, (entries) => {
  const isSelectionValid = entries.some(entry => entry.id === selectedCategory.value);
  if (!isSelectionValid) {
    selectedCategory.value = entries[0]?.id;
  }
}, { immediate: true });

// Expands every question of the active category. Stays collapsed when uncategorized, in the
// grouped all-topics view, or when the collapsed prop asks for it.
// Keyed on how many questions there are, not on their content, so editing an answer does not
// reopen the whole list.
watch([selectedCategory, () => accordionItems.value.length], ([category]) => {
  const shouldExpand = category && category !== ALL_TOPICS_ID && !props.collapsed;
  openItems.value = shouldExpand ? accordionItems.value.map(item => item.value) : [];
}, { immediate: true });

// Keeps the selected category in view in the mobile scrolling row. Reka UI only does this for
// keyboard navigation, so a category picked by tap can stay half-cut at the edge of the row.
// `nearest` on both axes leaves an already-visible category alone and, crucially, stops the
// horizontal scroll from dragging the page vertically.
watch(selectedCategory, async () => {
  if (!isMobile.value) return;
  await nextTick();
  railElement.value?.querySelector('.is-active')?.scrollIntoView({
    inline: 'nearest',
    block: 'nearest',
    behavior: 'smooth',
  });
});

const query = ref('');
const openBestMatch = ref<string[]>([]);
const openOtherMatches = ref<string[]>([]);

// Faqs can render more than once per page, so the input id cannot be a constant.
const searchInputId = useId();

const { results } = useFaqSearch(() => props.items, query);

// The content decides, not a flag: a tenant inheriting `searchable` through the Nuxt layer but
// authoring no FAQ items gets no bar, since every query there could only dead-end. It opts in by
// filling the items its app reads, which needs no release.
const isSearchable = computed(() => props.searchable && (props.items?.length ?? 0) > 0);

// An active query takes over the body, so the rail is only bypassed while searching.
const isSearching = computed(() => isSearchable.value && query.value.trim().length > 0);

const bestMatchItems = computed<AccordionItem[]>(() => {
  return results.value.slice(0, BEST_MATCH_COUNT).map(result => toAccordionItem(result.item, 'best-match'));
});

const otherMatchItems = computed<AccordionItem[]>(() => {
  return results.value.slice(BEST_MATCH_COUNT).map((result, index) => toAccordionItem(result.item, `match-${index}`));
});

const searchStatus = computed(() => {
  if (!isSearching.value) return '';
  const count = results.value.length;
  return t('ui.components.faqs.searchResultsStatus', { count }, count);
});

// Search results render expanded, so the answers are readable without a second click.
// Keyed on which questions matched, not on the item arrays: those are rebuilt on every keystroke,
// so watching them would reopen a panel the user had collapsed.
watch(() => titlesOf(bestMatchItems.value), () => {
  openBestMatch.value = bestMatchItems.value.map(item => item.value);
});

watch(() => titlesOf(otherMatchItems.value), () => {
  openOtherMatches.value = otherMatchItems.value.map(item => item.value);
});

function titlesOf(items: AccordionItem[]): string {
  return items.map(item => item.title).join('\n');
}
</script>

<style lang="scss">
  @use '@cvo/ui/styles/design-system' as vars;
  @use '@cvo/ui/styles/utils/text';
  @use '@cvo/ui/styles/utils/responsive';

  @layer cvo-ui {
    .Faqs {
      &-header {
        display: flex;
        flex-direction: column;
        align-items: center;
        text-align: center;
        gap: vars.$dimensions-d-4;
        margin-bottom: vars.$dimensions-d-12;

        @include responsive.mobile {
          gap: vars.$dimensions-d-3;
          margin-bottom: vars.$dimensions-d-6;
        }
      }

      &-title {
        color: vars.$patterns-faq-title-color;
        @include text.heading-responsive('h4', 'h1');
      }

      &-description,
      &-subtitle {
        color: vars.$patterns-faq-subtitle-color;
        @include text.text-responsive('md', 'lg');
      }

      &-subtitle {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        flex-wrap: wrap;
        gap: vars.$dimensions-d-1;
      }

      &-search {
        margin-inline: auto;
        margin-bottom: vars.$dimensions-d-6;
        // The 900px search bar of the design's lg/md spec.
        max-width: 56.25rem;
      }

      &-noResults {
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: vars.$dimensions-d-6;
        padding-block: vars.$dimensions-d-5;
        text-align: center;

        // Scoped to the glyph rather than every descendant icon, so the colour cannot leak into
        // whatever the app renders in the actions slot.
        &Icon {
          color: vars.$color-neutral-gray-500;
        }

        &Content {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: vars.$dimensions-d-3;
        }

        &Title {
          color: vars.$patterns-faq-subtitle-color;
          max-width: 37.5rem;
          @include text.heading('h5');
        }

        &Description {
          color: vars.$patterns-faq-text-color;
          @include text.text('sm');
        }

        // &Actions is deliberately layout-free: the slotted content owns how it is arranged.
      }

      &-resultsLabel {
        color: vars.$color-neutral-gray-700;
        margin-block: vars.$dimensions-d-4 vars.$dimensions-d-2;
        @include text.text('sm');

        &:first-child {
          margin-block-start: 0;
        }
      }

      // The top match is called out with the brand colour, the other matches keep the default chrome.
      &-bestMatch .Accordion-item {
        border-color: vars.$color-brand-primary-900;
      }
    }

    .Faqs--categorized {
      .Faqs-body {
        display: grid;
        gap: vars.$dimensions-d-4;

        @include responsive.tablet {
          grid-template-columns: auto minmax(0, 1fr);
          gap: vars.$dimensions-d-8;
          align-items: start;
        }
      }

      // Scrollable row on mobile, vertical rail from tablet up.
      .Faqs-categories {
        display: flex;
        // Navigation is a column by default; the mobile rail scrolls as a row
        flex-direction: row;
        gap: vars.$dimensions-d-1;
        max-width: 100%;
        overflow-x: auto;
        // The progress bar reports the scroll position instead of the native bar.
        scrollbar-width: none;

        &::-webkit-scrollbar {
          display: none;
        }

        // The selected category is raised with a shadow that the horizontal scroll would
        // otherwise clip on every side, so the row keeps room for it all round.
        padding: vars.$dimensions-d-2;

        @include responsive.tablet {
          flex-direction: column;
          align-items: stretch;
          overflow-x: visible;
          padding: vars.$dimensions-d-0;
          position: sticky;
          top: vars.$dimensions-d-4;
          // Sized by its longest label, capped at the navigation width: the rest of the area
          // becomes the gap before the questions. `stretch` above then squares up every button.
          width: fit-content;
          max-width: 500px;
          min-width: 260px;

          // The atom keeps labels on one line, which the narrower navigation can no longer
          // guarantee across every locale.
          .Button {
            width: 100%;
            white-space: normal;
          }
        }

        // Keeps the categories at their own width in the mobile scrolling row.
        .Button {
          flex-shrink: 0;
          width: auto;
        }
      }

      // Grouped all-topics view: one section per category, split by thin rules.
      .Faqs-group {
        display: flex;
        flex-direction: column;
        gap: vars.$dimensions-d-3;

        + .Faqs-group {
          margin-block-start: vars.$dimensions-d-8;
          border-block-start: vars.$dimensions-d-0-25 solid vars.$molecules-accordion-border-color;
          padding-block-start: vars.$dimensions-d-4;
        }
      }

      .Faqs-groupHeader {
        align-self: flex-start;
      }
    }

    // The aside pushes the panels into an explicit two-row grid so it can sit right
    // below the category rail. Only applied with the slot present: the landing keeps
    // the original auto-placed grid (and its sticky rail) untouched.
    .Faqs--withAside .Faqs-body {
      @include responsive.tablet {
        grid-template-rows: auto 1fr;
        row-gap: vars.$dimensions-d-4;

        .Faqs-categories {
          grid-column: 1;
          grid-row: 1;
          position: static;
        }

        .Faqs-panel {
          grid-column: 2;
          grid-row: 1 / -1;
        }

        .Faqs-aside {
          grid-column: 1;
          grid-row: 2;
          width: 0;
          min-width: 100%;
        }



      }
    }

    .Faqs-aside {
      border-block-start: vars.$dimensions-d-0-25 solid vars.$molecules-accordion-border-color;
      padding-block-start: vars.$dimensions-d-4;
    }
  }
</style>